import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  Map as MlMap, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type GeoJSONSource,
} from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'
import { buildRasterStyle, rasterBasemap } from '../map/basemaps'
import { useConfig } from '../lib/useConfig'
import type { PhotorealHandle } from '../map/photoreal'
import { Panel } from '../cc/Panel'
import type { StarHub, StarRoad, StarVehicle } from './useStarApi'

let workerSet = false
function ensureWorker(): void {
  if (workerSet) return
  setWorkerUrl(workerUrl)
  workerSet = true
}

/** STAR is one site (the Meghalaya plant + mine) -- unlike EIM's GTI/JM Baxi split, there
 *  is never a second viewport to choose between. Centre is the Plant Weighbridge hub
 *  (db/star_hub_seed.sql), overwritten by fitToVehicles the moment a real fix arrives. */
const FALLBACK_CENTER: [number, number] = [92.38729095458979, 25.1743392944336]

// ============================================================ truck icon (canvas-drawn)
// Same hand-drawn silhouette console/src/eim/EimPortsMap.tsx uses, ported rather than
// reinvented so a truck looks like a truck the same way everywhere in this console.
// TWO colours -- star_vehicle_telemetry.charging_status IS reported (classify_stop's
// idling suppression already reads it, serving/app.py), so a charging truck gets the
// dataviz skill's status-good green; every other state (moving or genuinely stationary)
// keeps the plain fleet blue. Not a third "idle" colour: STAR has no idle_since/power-draw
// split the way EIM does, so that finer state isn't invented here. Speed/ignition stay in
// the popup text, never color-alone for those.
const ICON_W = 38
const ICON_H = 66
const TYRE = '#1F2937'
const GLASS = 'rgba(255,255,255,0.85)'
const OUTLINE = 'rgba(255,255,255,0.94)'
const TRUCK_COLOR = '#0369A1'
const TRUCK_CHARGING_COLOR = '#0ca30c' // dataviz skill's status-good, reserved for this
const TRUCK_ICON_ID = 'star-truck'
const TRUCK_CHARGING_ICON_ID = 'star-truck-charging'

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2)
  g.beginPath()
  g.moveTo(x + rr, y)
  g.lineTo(x + w - rr, y)
  g.quadraticCurveTo(x + w, y, x + w, y + rr)
  g.lineTo(x + w, y + h - rr)
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  g.lineTo(x + rr, y + h)
  g.quadraticCurveTo(x, y + h, x, y + h - rr)
  g.lineTo(x, y + rr)
  g.quadraticCurveTo(x, y, x, y + rr)
  g.closePath()
}

function axle(g: CanvasRenderingContext2D, cy: number, halfTrack: number, len: number): void {
  const tw = 5
  g.fillStyle = TYRE
  roundRect(g, ICON_W / 2 - halfTrack - tw, cy - len / 2, tw, len, 1.8)
  g.fill()
  roundRect(g, ICON_W / 2 + halfTrack, cy - len / 2, tw, len, 1.8)
  g.fill()
}

function truckIcon(ratio: number, color: string = TRUCK_COLOR): ImageData {
  const c = document.createElement('canvas')
  c.width = ICON_W * ratio
  c.height = ICON_H * ratio
  const g = c.getContext('2d')!
  g.scale(ratio, ratio)
  const cx = ICON_W / 2
  g.lineJoin = 'round'
  g.lineCap = 'round'

  axle(g, 17, 12, 9)
  axle(g, 40, 12.5, 9)
  axle(g, 51, 12.5, 9)
  const deckTop = 27

  const bx = cx - 13, bw = 26, bh = ICON_H - 3 - deckTop
  roundRect(g, bx, deckTop, bw, bh, 3)
  g.fillStyle = color
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 1.8
  g.stroke()

  g.fillStyle = TYRE
  roundRect(g, cx - 16.5, 9.5, 5, 2.6, 1.2)
  g.fill()
  roundRect(g, cx + 11.5, 9.5, 5, 2.6, 1.2)
  g.fill()

  roundRect(g, cx - 12, 3, 24, 25, 4.5)
  g.fillStyle = color
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 1.8
  g.stroke()

  roundRect(g, cx - 8, 6.5, 16, 5.5, 2)
  g.fillStyle = GLASS
  g.fill()

  return g.getImageData(0, 0, ICON_W * ratio, ICON_H * ratio)
}
// ============================================================ /truck icon

// ============================================================== hub icons
// One lettered badge per star_hub.kind, using the dataviz skill's validated 6-slot
// categorical palette (references/palette.md) -- worst adjacent CVD deltaE 9.1 (light) /
// 8.4 (dark), both above the 8 target; the light-mode contrast WARN on 3 of the 6 slots
// (aqua/yellow/magenta) is the documented, expected trade-off, mitigated exactly as the
// skill requires: every hub carries a visible label (hover popup + the legend below),
// never color alone. A hub is also NEVER visually confusable with a vehicle -- it's a
// badge with a letter, not a truck silhouette.
const HUB_KIND_STYLE: Record<string, { color: string; letter: string; label: string }> = {
  weighbridge: { color: '#2a78d6', letter: 'W', label: 'Weighbridge' },
  gate:        { color: '#eb6834', letter: 'G', label: 'Gate' },
  entry:       { color: '#1baf7a', letter: 'E', label: 'Entry' },
  office:      { color: '#eda100', letter: 'O', label: 'Office' },
  parking:     { color: '#e87ba4', letter: 'P', label: 'Parking / Charging' },
  drop_point:  { color: '#008300', letter: 'D', label: 'Drop point' },
}
const HUB_KIND_FALLBACK = { color: '#52514e', letter: '?', label: 'Other' }

function hubKindStyle(kind: string | null): { color: string; letter: string; label: string } {
  return (kind && HUB_KIND_STYLE[kind]) || HUB_KIND_FALLBACK
}

function hubIconId(kind: string | null): string {
  return `star-hub-${kind && HUB_KIND_STYLE[kind] ? kind : 'other'}`
}

function hubBadgeIcon(style: { color: string; letter: string }, ratio: number): ImageData {
  const w = 22, h = 22
  const c = document.createElement('canvas')
  c.width = w * ratio
  c.height = h * ratio
  const g = c.getContext('2d')!
  g.scale(ratio, ratio)
  g.beginPath()
  g.arc(w / 2, h / 2, 9, 0, Math.PI * 2)
  g.fillStyle = style.color
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 2
  g.stroke()
  g.fillStyle = '#ffffff'
  g.font = '700 11px Inter, sans-serif'
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.fillText(style.letter, w / 2, h / 2 + 0.5)
  return g.getImageData(0, 0, w * ratio, h * ratio)
}

function clusterIcon(count: number, ratio: number): ImageData {
  // A cluster is a DIFFERENT shape (rounded square, not a circle or a truck) so it never
  // reads as "one more vehicle" or "a hub" at a glance -- it explicitly means "more than
  // one truck at this pixel," which is the exact ambiguity that was invisible before
  // (multiple trucks queued at the same spot rendered as a single, indistinguishable icon).
  const w = 30, h = 24
  const c = document.createElement('canvas')
  c.width = w * ratio
  c.height = h * ratio
  const g = c.getContext('2d')!
  g.scale(ratio, ratio)
  roundRect(g, 1, 1, w - 2, h - 2, 6)
  g.fillStyle = TRUCK_COLOR
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 2
  g.stroke()
  g.fillStyle = '#ffffff'
  g.font = '700 13px Inter, sans-serif'
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.fillText(String(count), w / 2, h / 2 + 1)
  return g.getImageData(0, 0, w * ratio, h * ratio)
}

function registerIcons(map: MlMap, hubs: StarHub[]): void {
  const ratio = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)))
  if (!map.hasImage(TRUCK_ICON_ID)) map.addImage(TRUCK_ICON_ID, truckIcon(ratio), { pixelRatio: ratio })
  if (!map.hasImage(TRUCK_CHARGING_ICON_ID)) {
    map.addImage(TRUCK_CHARGING_ICON_ID, truckIcon(ratio, TRUCK_CHARGING_COLOR), { pixelRatio: ratio })
  }
  const kinds = new Set(hubs.map((h) => (h.kind && HUB_KIND_STYLE[h.kind] ? h.kind : 'other')))
  kinds.add('other') // registered unconditionally -- a hub with an unrecognized kind must
                      // still render as SOMETHING, not silently vanish from the map.
  for (const kind of kinds) {
    const id = kind === 'other' ? hubIconId(null) : hubIconId(kind)
    if (map.hasImage(id)) continue
    const style = kind === 'other' ? HUB_KIND_FALLBACK : HUB_KIND_STYLE[kind]!
    map.addImage(id, hubBadgeIcon(style, ratio), { pixelRatio: ratio })
  }
  // A fixed small set of cluster-count icons (2..CLUSTER_ICON_MAX) covers STAR's actual
  // fleet size (5 trucks) exactly -- clusterIcon() draws vector text, not a bitmap font
  // table, so this loop is cheap and exact rather than an approximation.
  for (let n = 2; n <= CLUSTER_ICON_MAX; n++) {
    const id = clusterIconId(n)
    if (map.hasImage(id)) continue
    map.addImage(id, clusterIcon(n, ratio), { pixelRatio: ratio })
  }
}

const CLUSTER_ICON_MAX = 12
function clusterIconId(count: number): string {
  return `star-cluster-${Math.min(count, CLUSTER_ICON_MAX)}`
}
// ============================================================= /hub icons

// ============================================================= road geometry
// Real OSM road geometry within the plant+mine site (tools/fetch_star_road_geometry.py,
// GET /api/star/roads) -- NOT the empirical H3 speed-profile grid. An earlier cut drew
// that grid on the map (first as filled hexagons, then as a graph of edges between
// H3-adjacent cells) and both read as an approximation rather than an actual road; the
// H3 data stays backend-only now (classify_overspeed still reads it via
// _star_speed_profile_lookup), and the map draws the real thing instead. Same
// casing-under-line pattern console/src/map/layers/reference.ts uses for Ultratech's own
// (manually-surveyed) route KML -- one visual language for "this is a road" everywhere
// in this console, not a second one invented for STAR.
function roadsToGeoJSON(roads: StarRoad[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: roads
      .filter((r) => r.coordinates.length >= 2)
      .map((r) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: r.coordinates },
        properties: { name: r.name, highwayClass: r.highway_class },
      })),
  }
}

function roadPopupHtml(p: Record<string, unknown>): string | null {
  if (!p.name) return null // most ways here are unnamed plant/mine service roads --
                            // a popup with nothing in it is worse than no popup at all
  return (
    `<div style="min-width:120px;font-family:Inter,sans-serif">`
    + `<div style="font:700 12px Inter,sans-serif;color:#0F172A">${p.name}</div>`
    + (p.highwayClass ? `<div style="font:11px Inter,sans-serif;color:#94A3B8;margin-top:2px">${p.highwayClass}</div>` : '')
    + `</div>`
  )
}
// ============================================================ /road geometry

function vehiclesToGeoJSON(
  vehicles: StarVehicle[], positions?: Map<string, [number, number]>, headings?: Map<string, number>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vehicles
      .filter((v): v is StarVehicle & { latitude: number; longitude: number } =>
        v.latitude != null && v.longitude != null)
      .map((v) => ({
        type: 'Feature',
        // positions overrides [v.longitude, v.latitude] mid-tween (see animateTruckMove
        // below) -- everywhere else (initial load, a vehicle with no prior fix to tween
        // from) falls through to the real reported position.
        geometry: { type: 'Point', coordinates: positions?.get(v.vehicle_id) ?? [v.longitude, v.latitude] },
        properties: {
          id: v.vehicle_id,
          soc: v.soc,
          speed: v.speed,
          ignition: v.ignition_status,
          charging: v.charging_status,
          iconId: v.charging_status === 1 ? TRUCK_CHARGING_ICON_ID : TRUCK_ICON_ID,
          // 0 (pointing north, the icon's own drawn orientation) until a real heading is
          // known -- see headingDeg below for why PROPEL reporting no course field means
          // this has to be computed from consecutive fixes rather than read off the wire.
          bearing: headings?.get(v.vehicle_id) ?? 0,
          polledTs: v.polled_ts,
        },
      })),
  }
}

// ================================================ heading (course over ground)
// PROPEL reports no heading/course field at all (confirmed: star_rules.parse_star_message
// has no such field, and neither does star_vehicle_telemetry). The truck icon is drawn
// facing up the canvas -- cab near the top, deck below -- so bearing 0 means "north" and
// MapLibre's icon-rotate turns it to match a computed one. Without this, a truck driving
// southeast rendered as a fixed north-facing silhouette gliding diagonally, which reads
// as sliding sideways rather than driving -- confirmed live, this is what "vehicles
// moving sideways" meant.
const EARTH_RADIUS_M = 6_371_000
// Below this displacement between polls, leave the heading alone -- GPS jitter on a
// parked truck (confirmed live: star_vehicle_telemetry positions wobble a fraction of a
// metre to a couple of metres poll-to-poll while stationary) would otherwise spin the
// icon at random. 15m clears that noise floor with room; a real move at STAR's slowest
// flagged speed still covers several times this in one 10s poll gap.
const HEADING_MIN_DISPLACEMENT_M = 15

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

function bearingDeg(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const toDeg = (r: number) => (r * 180) / Math.PI
  const dLng = toRad(lng2 - lng1)
  const y = Math.sin(dLng) * Math.cos(toRad(lat2))
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
    - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

// RAW TWO-FIX BEARING IS NOISIER THAN IT LOOKS. Confirmed live against a genuinely
// moving truck (~22kph, real consecutive 10s-poll fixes): the raw bearing swung
// 63deg -> 98deg -> 83deg between updates despite the truck travelling a fairly
// straight line at steady speed -- ordinary GPS position noise is a big enough
// fraction of the ~60m covered per poll to swing a 2-point angle estimate by tens of
// degrees. A truck icon re-pointed by that much every ~10s reads as wobbling off the
// road rather than driving along it -- confirmed live, this is what "moving
// diagonally, not facing towards routes" meant even after rotation was wired up
// correctly. Raising HEADING_MIN_DISPLACEMENT_M doesn't fix this (a real ~60m move
// already clears it and still showed the swing) -- the fix is smoothing the answer
// over successive updates, not demanding a bigger single sample.
//
// Circular (vector) exponential smoothing, not a plain running average of degrees --
// degrees wrap at 360/0, so a truck heading from 350deg to 10deg must smooth through
// north (360/0), not swing the long way through 180. Averaging each bearing's unit
// vector and re-deriving the angle handles the wrap for free.
const HEADING_SMOOTHING = 0.5 // weight on the newest raw reading; the rest stays on the prior smoothed heading

function blendHeading(prevDeg: number | undefined, rawDeg: number): number {
  if (prevDeg == null) return rawDeg // first reading for this vehicle -- nothing to blend with yet
  const toRad = (d: number) => (d * Math.PI) / 180
  const toDeg = (r: number) => (r * 180) / Math.PI
  const px = Math.cos(toRad(prevDeg)), py = Math.sin(toRad(prevDeg))
  const rx = Math.cos(toRad(rawDeg)), ry = Math.sin(toRad(rawDeg))
  const bx = px * (1 - HEADING_SMOOTHING) + rx * HEADING_SMOOTHING
  const by = py * (1 - HEADING_SMOOTHING) + ry * HEADING_SMOOTHING
  return (toDeg(Math.atan2(by, bx)) + 360) % 360
}

/** Mutates `headings` in place -- called once per poll (not per animation frame) with
 *  the PRE-tween drawn positions, so a heading is a stable "which way is this truck
 *  driving this cycle" answer rather than something recomputed 60 times a second off
 *  two nearly-identical interpolated points. */
function updateHeadings(
  headings: Map<string, number>, from: Map<string, [number, number]>, to: StarVehicle[],
): void {
  for (const v of to) {
    if (v.latitude == null || v.longitude == null) continue
    const prev = from.get(v.vehicle_id)
    if (!prev) continue // no prior fix to compare against -- keep the icon's default heading
    const distM = haversineM(prev[1], prev[0], v.latitude, v.longitude)
    if (distM >= HEADING_MIN_DISPLACEMENT_M) {
      const raw = bearingDeg(prev[1], prev[0], v.latitude, v.longitude)
      headings.set(v.vehicle_id, blendHeading(headings.get(v.vehicle_id), raw))
    }
  }
}
// =============================================== /heading (course over ground)

// ========================================================= truck position tween
// The 10s poll (StarShell's refresh interval) is far coarser than the eye's motion
// threshold: src.setData() below used to jump a truck straight to its new fix, so a
// genuinely-moving vehicle at low speed (a few kph -- tens of metres over 10s) read as
// perfectly stationary between snapshots, indistinguishable from one that hadn't moved
// at all. Ultratech's MapCanvas.tsx solves the analogous problem with a full RAF-driven
// dead-reckoning loop that projects position from speed+heading between sparse ~32s
// packets -- overkill here, where STAR's own telemetry already arrives every 5s (see
// _star_kafka_loop). A straight lerp between the last-drawn and newly-reported fix over
// the poll window gives the same "it's actually moving" perception at a fraction of the
// complexity, and is the right-sized port of that idea rather than the whole system.
//
// SPANS THE WHOLE POLL WINDOW, NOT A SHORT BURST WITHIN IT. An earlier cut used a 2.6s
// glide inside the 10s gap -- correct in principle, but a truck then sat frozen for the
// remaining 7.4s (74% of every cycle), which is what a glance at the screen actually
// samples most of the time. It read as "always stationary" even though it technically
// animated. 9.5s (just under the 10s poll) keeps the truck interpolating continuously
// for nearly the entire cycle instead. LINEAR, not eased: STAR's telemetry reports a
// roughly constant speed within one poll window, and a real truck does not decelerate
// into its next GPS fix -- an ease-out here would visibly slow the truck down right
// before every poll for no physical reason.
const TWEEN_MS = 9500 // just under the 10s poll interval -- glides continuously, never overlaps the next poll

function animateTruckMove(
  from: Map<string, [number, number]>, to: StarVehicle[],
  onFrame: (positions: Map<string, [number, number]>) => void,
): () => void {
  const start = performance.now()
  let raf = 0
  const tick = (now: number): void => {
    const eased = Math.min(1, (now - start) / TWEEN_MS)
    const positions = new Map<string, [number, number]>()
    for (const v of to) {
      if (v.latitude == null || v.longitude == null) continue
      const prev = from.get(v.vehicle_id)
      positions.set(
        v.vehicle_id,
        prev
          ? [prev[0] + (v.longitude - prev[0]) * eased, prev[1] + (v.latitude - prev[1]) * eased]
          : [v.longitude, v.latitude],
      )
    }
    onFrame(positions)
    if (eased < 1) raf = requestAnimationFrame(tick)
  }
  raf = requestAnimationFrame(tick)
  return () => cancelAnimationFrame(raf)
}
// ======================================================== /truck position tween

function hubsToGeoJSON(hubs: StarHub[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: hubs.map((h) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [h.longitude, h.latitude] },
      properties: {
        name: h.name,
        kind: h.kind,
        bufferM: h.buffer_m,
        iconId: hubIconId(h.kind),
      },
    })),
  }
}

function median(ns: number[]): number {
  const s = [...ns].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2
}

/** Fits to the dense cluster, not every point -- same reasoning EimPortsMap's own
 *  fitToVehicles documents: one truck genuinely away from site must not zoom the whole
 *  plant out to a speck. 3km comfortably covers plant+mine (observed spread here is
 *  under 2km per the KML survey) and is comfortably smaller than "actually left site". */
const CLUSTER_RADIUS_KM = 3

function fitToVehicles(map: MlMap, vehicles: StarVehicle[]): void {
  const pts = vehicles.filter((v): v is StarVehicle & { latitude: number; longitude: number } =>
    v.latitude != null && v.longitude != null)
  if (pts.length === 0) return
  if (pts.length === 1) {
    map.easeTo({ center: [pts[0]!.longitude, pts[0]!.latitude], zoom: 14, duration: 500 })
    return
  }
  const medLat = median(pts.map((v) => v.latitude))
  const medLng = median(pts.map((v) => v.longitude))
  const core = pts.filter((v) => {
    const dLatKm = (v.latitude - medLat) * 111
    const dLngKm = (v.longitude - medLng) * 105
    return Math.hypot(dLatKm, dLngKm) <= CLUSTER_RADIUS_KM
  })
  const fitset = core.length > 0 ? core : pts
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
  for (const v of fitset) {
    minLng = Math.min(minLng, v.longitude); maxLng = Math.max(maxLng, v.longitude)
    minLat = Math.min(minLat, v.latitude); maxLat = Math.max(maxLat, v.latitude)
  }
  map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 56, duration: 500, maxZoom: 15 })
}

function vehiclePopupHtml(p: Record<string, unknown>): string {
  const soc = p.soc != null ? `${Math.round(Number(p.soc))}% SOC` : null
  const speed = p.speed != null ? `${Number(p.speed).toFixed(0)} km/h` : null
  const ignition = p.ignition === 1 ? 'Ignition on' : p.ignition === 0 ? 'Ignition off' : null
  const charging = p.charging === 1
  return (
    `<div style="min-width:160px;font-family:Inter,sans-serif">`
    + `<div style="font:700 12px ui-monospace,monospace;color:#0F172A">${p.id}</div>`
    + `<div style="font:11px Inter,sans-serif;color:#64748B;margin-top:4px">${[soc, speed].filter(Boolean).join(' · ') || '—'}</div>`
    + (ignition ? `<div style="font:11px Inter,sans-serif;color:#94A3B8;margin-top:2px">${ignition}</div>` : '')
    // Text label, never color alone -- the truck icon is already green while charging.
    + (charging ? `<div style="font:700 11px Inter,sans-serif;color:${TRUCK_CHARGING_COLOR};margin-top:2px">Charging</div>` : '')
    + `</div>`
  )
}

function hubPopupHtml(p: Record<string, unknown>): string {
  const style = hubKindStyle(p.kind as string | null)
  return (
    `<div style="min-width:170px;font-family:Inter,sans-serif">`
    + `<div style="font:700 12px Inter,sans-serif;color:#0F172A">${p.name}</div>`
    + `<div style="font:11px Inter,sans-serif;color:${style.color};margin-top:2px;font-weight:600">${style.label}</div>`
    + `<div style="font:11px Inter,sans-serif;color:#94A3B8;margin-top:2px">idling/unscheduled-stop suppressed within ${p.bufferM}m</div>`
    + `</div>`
  )
}

/** Below the map, not on it -- an in-map legend competes with the basemap and the
 *  vehicle markers for a small viewport; a panel row reads cleanly at any zoom and never
 *  needs its own collision handling. */
function HubLegend(): JSX.Element {
  return (
    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 px-1">
      <div className="flex items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: TRUCK_CHARGING_COLOR }}
        />
        <span className="text-[11px] text-[#64748B]">Charging</span>
      </div>
      {Object.entries(HUB_KIND_STYLE).map(([kind, style]) => (
        <div key={kind} className="flex items-center gap-1.5">
          <span
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
            style={{ backgroundColor: style.color }}
          >
            {style.letter}
          </span>
          <span className="text-[11px] text-[#64748B]">{style.label}</span>
        </div>
      ))}
    </div>
  )
}

export function StarMap({ vehicles, hubs, roads, focusVehicleId, onVehicleClick }: {
  vehicles: StarVehicle[]; hubs: StarHub[]; roads: StarRoad[]; focusVehicleId?: string | null
  /** Mark breakdown, live, from the map itself -- a click on the truck's own icon, not
   *  just the vehicle list below the fold. onVehicleClick is optional so StarMap stays
   *  usable read-only wherever nothing should be markable from it. */
  onVehicleClick?: (vehicleId: string) => void
}): JSX.Element {
  const container = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MlMap | null>(null)
  const vehiclesRef = useRef<StarVehicle[]>(vehicles)
  const hubsRef = useRef<StarHub[]>(hubs)
  const roadsRef = useRef<StarRoad[]>(roads)
  const fittedRef = useRef(false)
  // Last position actually drawn per vehicle -- the tween's start point for the NEXT
  // update, not just a snapshot of the latest poll. Empty on first paint, so that first
  // render draws real fixes immediately with no tween (nothing to glide from yet).
  const drawnPositionsRef = useRef<Map<string, [number, number]>>(new Map())
  // Last known "which way is this truck driving" per vehicle -- see updateHeadings.
  const headingsRef = useRef<Map<string, number>>(new Map())
  const cancelTweenRef = useRef<(() => void) | null>(null)
  // Ref, not a direct closure over the prop -- the 'load' listener below is registered
  // once (empty effect deps, same as every other one-time map setup here) and would
  // otherwise capture whatever onVehicleClick was on the FIRST render forever.
  const onVehicleClickRef = useRef(onVehicleClick)
  vehiclesRef.current = vehicles
  hubsRef.current = hubs
  roadsRef.current = roads
  onVehicleClickRef.current = onVehicleClick

  // Photoreal 3D, opt-in -- same pattern as EimPortsMap.tsx/BusRouteMap.tsx. Purely
  // additive; the default OSM raster underneath is unchanged.
  const [mapLoaded, setMapLoaded] = useState(false)
  const [photorealOn, setPhotorealOn] = useState(false)
  const { photoreal: photorealAvailableCfg, maps_key: mapsKeyCfg } = useConfig()
  const photorealAvailable = Boolean(photorealAvailableCfg)
  const mapsKey = mapsKeyCfg || ''
  const [attribution, setAttribution] = useState<string[]>([])
  const photorealRef = useRef<PhotorealHandle | null>(null)

  useEffect(() => {
    if (!container.current) return
    ensureWorker()
    const basemap = rasterBasemap('osm')
    if (!basemap) return
    const map = new MlMap({
      container: container.current,
      style: buildRasterStyle(basemap),
      center: FALLBACK_CENTER,
      zoom: 13,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')

    const hoverPopup = new Popup({ closeButton: false, closeOnClick: false, offset: 18 })

    map.on('load', () => {
      registerIcons(map, hubsRef.current)

      // Added FIRST -- layers render bottom-up in add order, and the road overlay must
      // sit beneath the hub badges and truck icons, not obscure them. Casing-under-line,
      // the exact pattern console/src/map/layers/reference.ts uses for Ultratech's route
      // KML -- pale stroke behind a darker one, the trick that keeps a polyline legible
      // over a busy basemap at every zoom.
      map.addSource('star-roads', { type: 'geojson', data: roadsToGeoJSON(roadsRef.current) })
      map.addLayer({
        id: 'star-roads-casing',
        type: 'line',
        source: 'star-roads',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#FFFFFF',
          'line-opacity': 0.85,
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 16, 7],
        },
      })
      map.addLayer({
        id: 'star-roads-line',
        type: 'line',
        source: 'star-roads',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0F172A',
          'line-opacity': 0.72,
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1.4, 16, 3.2],
        },
      })

      map.addSource('star-hubs', { type: 'geojson', data: hubsToGeoJSON(hubsRef.current) })
      map.addLayer({
        id: 'star-hubs',
        type: 'symbol',
        source: 'star-hubs',
        layout: {
          'icon-image': ['get', 'iconId'],
          'icon-allow-overlap': true,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.7, 14, 1.0, 17, 1.2],
        },
      })

      // CLUSTERED -- multiple trucks queued at the same weighbridge/gate/charging bay
      // (confirmed live: 4 STAR trucks within ~20m of each other, all stationary) used
      // to render as ONE indistinguishable icon with icon-allow-overlap. clusterRadius
      // is small (40px) so this only merges points that are genuinely on top of each
      // other at the current zoom, not ones merely near the same hub -- zooming in
      // separates them into individual trucks well before clusterMaxZoom.
      map.addSource('star-trucks', {
        type: 'geojson',
        data: vehiclesToGeoJSON(vehiclesRef.current),
        cluster: true,
        clusterRadius: 40,
        clusterMaxZoom: 16,
      })
      map.addLayer({
        id: 'star-truck-clusters',
        type: 'symbol',
        source: 'star-trucks',
        filter: ['has', 'point_count'],
        layout: {
          'icon-image': ['concat', 'star-cluster-', ['min', ['get', 'point_count'], CLUSTER_ICON_MAX]],
          'icon-allow-overlap': true,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.7, 14, 0.9, 17, 1.05],
        },
      })
      map.addLayer({
        id: 'star-trucks',
        type: 'symbol',
        source: 'star-trucks',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': ['get', 'iconId'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.32, 14, 0.5, 17, 0.75],
          // Turns the icon to face its actual direction of travel (see updateHeadings) --
          // 'map' alignment, not 'viewport', so the rotation is relative to true north and
          // stays correct as the user pans/rotates the map, not just the screen.
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
        },
      })

      if (vehiclesRef.current.some((v) => v.latitude != null && v.longitude != null)) {
        fitToVehicles(map, vehiclesRef.current)
        fittedRef.current = true
      }

      map.on('click', 'star-truck-clusters', (e) => {
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        const clusterId = f.properties?.cluster_id as number | undefined
        const src = map.getSource('star-trucks') as GeoJSONSource
        if (clusterId == null || !src.getClusterExpansionZoom) return
        src.getClusterExpansionZoom(clusterId).then((zoom) => {
          map.easeTo({ center: f.geometry.type === 'Point' ? (f.geometry.coordinates as [number, number]) : undefined, zoom, duration: 500 })
        }).catch(() => {})
      })

      // Mark breakdown, straight from the truck's own icon -- a single click, not routed
      // through the vehicle list. Individual trucks only (the filter excludes clusters,
      // which the handler above already owns for zoom-to-expand); at STAR's zoom levels a
      // cluster click expands before a user could mean to breakdown-mark a merged icon.
      map.on('click', 'star-trucks', (e) => {
        const f = e.features?.[0]
        const id = f?.properties?.id as string | undefined
        if (id) onVehicleClickRef.current?.(id)
      })

      for (const layer of ['star-trucks', 'star-truck-clusters', 'star-hubs', 'star-roads-casing']) {
        map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', layer, () => {
          map.getCanvas().style.cursor = ''
          hoverPopup.remove()
        })
      }
      for (const layer of ['star-trucks', 'star-hubs']) {
        map.on('mouseenter', layer, (e) => {
          const f = e.features?.[0]
          if (!f || f.geometry.type !== 'Point') return
          const html = layer === 'star-trucks' ? vehiclePopupHtml(f.properties ?? {}) : hubPopupHtml(f.properties ?? {})
          hoverPopup.setLngLat(f.geometry.coordinates as [number, number]).setHTML(html).addTo(map)
        })
      }
      // LineString, not Point -- anchor the popup on the cursor (e.lngLat), not the
      // feature's own geometry. Uses the wider casing layer for hit-testing (easier to
      // land the cursor on) even though the visible line itself is the thinner top layer.
      map.on('mousemove', 'star-roads-casing', (e) => {
        const f = e.features?.[0]
        const html = f ? roadPopupHtml(f.properties ?? {}) : null
        if (html) hoverPopup.setLngLat(e.lngLat).setHTML(html).addTo(map)
        else hoverPopup.remove() // most ways here are unnamed -- no popup, not an empty one
      })
      setMapLoaded(true)
    })

    return () => {
      cancelTweenRef.current?.()
      photorealRef.current?.destroy()
      photorealRef.current = null
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return
    if (photorealOn && !photorealRef.current && mapsKey) {
      let cancelled = false
      void import('../map/photoreal').then(({ addPhotoreal }) => {
        if (cancelled || photorealRef.current || !mapRef.current) return
        photorealRef.current = addPhotoreal(mapRef.current, mapsKey, {
          onLoad: () => {},
          onAttribution: (credits) => setAttribution(credits),
          onError: () => {},
        })
      })
      return () => { cancelled = true }
    }
    if (!photorealOn && photorealRef.current) {
      photorealRef.current.destroy()
      photorealRef.current = null
      setAttribution([])
    }
  }, [photorealOn, mapsKey, mapLoaded])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const src = map.getSource('star-trucks') as GeoJSONSource | undefined
    if (!src) return
    cancelTweenRef.current?.()
    // Heading first, using the PRE-tween drawn positions -- animateTruckMove below
    // mutates drawnPositionsRef every frame, so this has to run before it starts.
    updateHeadings(headingsRef.current, drawnPositionsRef.current, vehicles)
    cancelTweenRef.current = animateTruckMove(drawnPositionsRef.current, vehicles, (positions) => {
      drawnPositionsRef.current = positions
      src.setData(vehiclesToGeoJSON(vehicles, positions, headingsRef.current))
    })
    if (!fittedRef.current && vehicles.some((v) => v.latitude != null && v.longitude != null)) {
      fitToVehicles(map, vehicles)
      fittedRef.current = true
    }
  }, [vehicles])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const src = map.getSource('star-hubs') as GeoJSONSource | undefined
    if (!src) return
    // registerIcons() at map 'load' time ran against whatever hubsRef.current held at
    // that instant -- and the hub fetch (StarShell's fetchStarHubs) is async, so 'load'
    // routinely fires before it resolves, registering only the 'other' fallback. Without
    // re-registering here, setData below pushes features whose iconId (e.g.
    // "star-hub-weighbridge") was never added via addImage, and MapLibre drops those
    // symbols silently -- the hubs source fills in but nothing draws.
    registerIcons(map, hubs)
    src.setData(hubsToGeoJSON(hubs))
  }, [hubs])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const src = map.getSource('star-roads') as GeoJSONSource | undefined
    if (!src) return
    src.setData(roadsToGeoJSON(roads))
  }, [roads])

  // Click-to-locate: a vehicle tile below the map sets focusVehicleId, and this flies the
  // viewport to that truck's CURRENT position (not a stale one -- reads live `vehicles`,
  // not a snapshot taken when the tile was clicked). No-ops if the map isn't ready yet or
  // the vehicle has no live fix, rather than throwing.
  useEffect(() => {
    if (!focusVehicleId) return
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const v = vehicles.find((x) => x.vehicle_id === focusVehicleId)
    if (!v || v.latitude == null || v.longitude == null) return
    map.flyTo({ center: [v.longitude, v.latitude], zoom: 16, duration: 700 })
  }, [focusVehicleId, vehicles])

  return (
    <Panel
      title={`Live positions${vehicles.length ? ` · ${vehicles.length}` : ''}`}
      pad={false}
      className="h-[492px]"
    >
      <div className="relative h-[440px] w-full">
        <div ref={container} className="h-full w-full" />
        <div className="pointer-events-auto absolute bottom-3 right-3 z-10 flex flex-col items-end gap-1">
          <button
            type="button"
            disabled={!photorealAvailable}
            onClick={() => setPhotorealOn((v) => !v)}
            title={
              photorealAvailable
                ? 'Google Photorealistic 3D Tiles. Billed per session, needs internet.'
                : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.'
            }
            className={clsx(
              'rounded-full px-3 py-1.5 text-[11px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.12)] backdrop-blur transition-colors disabled:cursor-not-allowed disabled:opacity-40',
              photorealOn ? 'bg-[#D97706] text-white' : 'bg-white/90 text-[#0F172A] hover:bg-white',
            )}
          >
            photoreal 3D
          </button>
          {photorealOn && attribution.length > 0 && (
            <div className="max-w-[220px] truncate rounded-full bg-white/80 px-2 py-0.5 text-[10px] text-[#64748B] backdrop-blur"
                 title={attribution.join(' · ')}>
              {attribution.join(' · ')}
            </div>
          )}
        </div>
      </div>
      <HubLegend />
    </Panel>
  )
}
